import { useRef, useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  RadioGroup,
  Typography,
  useTheme,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import ReactPlayer from 'react-player';
import { Controller, useFormContext } from 'react-hook-form';

import { ControlledDateField } from '@vyce/core/src/components/controlled-inputs/ControlledDateField';
import { AppCheckbox } from '@vyce/core/src/components/inputs/AppCheckbox';
import { AppRoundedRadio } from '@vyce/core/src/components/inputs/AppRadio';
import { AppTextField } from '@vyce/core/src/components/inputs/AppTextField';
import { AppKeyboardTimePicker } from '@vyce/core/src/components/inputs/AppKeyboardTimePicker';
import { OnboardingStepTitle } from '@vyce/core/src/components/OnboardingStepTitle';
import { FormattedDescription } from '@vyce/core/src/components/FormattedDescription';
import { FormAnswerMediaItem, FormQuestion as FormQuestionType } from '@vyce/core/src/types';
import { PDFPreview } from '@vyce/core/src/components/PreviewDocumentDialog/components/PDFPreview';
import { compressFile } from '@vyce/core/src/components/ImageUploader/utils';
import { uploadFileRequest } from '@vyce/core/src/api/storage';
import { safeHref } from '@vyce/core/src/utils/url';
import { IoCloseOutline } from 'react-icons/io5';

import { FormSignature } from './FormSignature';
import { FormResultMediaPreview } from './FormResultMediaPreview';
import '../formExportPrint.css';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';
interface Props {
  question: FormQuestionType;
  disabled?: boolean;
  signatureBase64?: string;
}

const mergePhotoAnswerValue = (value: unknown): { comment: string; media: FormAnswerMediaItem[] } => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as { comment?: unknown; media?: unknown };
    return {
      comment: typeof v.comment === 'string' ? v.comment : '',
      media: Array.isArray(v.media) ? (v.media as FormAnswerMediaItem[]) : [],
    };
  }
  return { comment: '', media: [] };
};

const REQUIRED_MSG = 'This field is required';

const getTextAnswerRules = (required: boolean | undefined) => {
  if (!required) {
    return undefined;
  }
  return {
    validate: (value: unknown) => {
      const text = typeof value === 'string' ? value : (value as { text?: string })?.text;
      return (typeof text === 'string' && text.trim() !== '') || REQUIRED_MSG;
    },
  };
};

const getTimeRules = (required: boolean | undefined) => {
  if (!required) {
    return undefined;
  }
  return {
    validate: (value: unknown) => {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return true;
      }
      return REQUIRED_MSG;
    },
  };
};

const getPhotoRules = (required: boolean | undefined) => {
  if (!required) {
    return undefined;
  }
  return {
    validate: (value: unknown) => {
      const { comment, media } = mergePhotoAnswerValue(value);
      const hasComment = comment.trim().length > 0;
      const hasMedia = media.some(m => Boolean(m?.url));
      return hasComment || hasMedia || REQUIRED_MSG;
    },
  };
};

export const FormAnswer: React.FC<Props> = ({ question, disabled, signatureBase64 }) => {
  const { control } = useFormContext();
  const theme = useTheme();
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  return (
    <Box style={{ padding: '16px 16px 24px 16px' }}>
      <OnboardingStepTitle
        text={question.title}
        helpText={
          question.explanation ? (
            <FormattedDescription text={question.explanation} variant="body2" />
          ) : undefined
        }
        additionalText={
          question.description ? (
            <FormattedDescription text={question.description} variant="body2" />
          ) : undefined
        }
      />

      {question.pdf?.url && (
        <>
          <Box className="form-summary-screen-only" style={{ marginTop: 24 }}>
            <PDFPreview url={question.pdf.url} />
          </Box>
          <Box className="form-summary-print-only" style={{ marginTop: 24 }}>
            <Typography variant="body2" color="textSecondary">
              PDF attachment:{' '}
              <a
                href={safeHref(question.pdf.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="form-summary-print-link"
              >
                {question.pdf.url}
              </a>
            </Typography>
          </Box>
        </>
      )}

      {question.photo && (
        <Box style={{ height: 250, marginTop: 24 }}>
          <img
            src={question.photo?.url}
            alt={question.title}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>
      )}

      {question.videoLink && (
        <>
          <Box className="form-summary-screen-only" style={{ height: 300, marginTop: 24 }}>
            <ReactPlayer width="100%" height="100%" controls url={question.videoLink} />
          </Box>
          <Box className="form-summary-print-only" style={{ marginTop: 24 }}>
            <Typography variant="body2" color="textSecondary">
              Video:{' '}
              <a
                href={safeHref(question.videoLink)}
                target="_blank"
                rel="noopener noreferrer"
                className="form-summary-print-link"
              >
                {question.videoLink}
              </a>
            </Typography>
          </Box>
        </>
      )}

      {question.answer.type === 'short_text' && (
        <Controller
          control={control}
          rules={getTextAnswerRules(question.answer.required)}
          name={question.id}
          render={({ field, formState }) => (
            <>
              <AppTextField
                {...field}
                disabled={disabled}
                error={!!formState.errors[question.id]}
                helperText={
                  formState.errors[question.id]?.message
                    ? String(formState.errors[question.id]?.message)
                    : undefined
                }
                label="Answer here"
                value={typeof field.value === 'string' ? field.value : field.value?.text}
                fullWidth
                margin="normal"
                inputProps={{ maxLength: 50 }}
              />
              {field.value?.media?.map((media: FormAnswerMediaItem) => (
                <FormResultMediaPreview media={[media]} title={question.title} />
              ))}
            </>
          )}
        />
      )}

      {question.answer.type === 'long_text' && (
        <Controller
          control={control}
          rules={getTextAnswerRules(question.answer.required)}
          name={question.id}
          render={({ field, formState }) => (
            <>
              <AppTextField
                {...field}
                disabled={disabled}
                error={!!formState.errors[question.id]}
                helperText={
                  formState.errors[question.id]?.message
                    ? String(formState.errors[question.id]?.message)
                    : undefined
                }
                label="Answer here"
                value={typeof field.value === 'string' ? field.value : field.value?.text}
                fullWidth
                multiline
                rows={3}
                margin="normal"
                inputProps={{ maxLength: 1000 }}
              />
              {field.value?.media?.map((media: FormAnswerMediaItem) => (
                <FormResultMediaPreview media={[media]} title={question.title} />
              ))}
            </>
          )}
        />
      )}

      {question.answer.type === 'single_choice' && (
        <Controller
          control={control}
          rules={question.answer.required ? { required: REQUIRED_MSG } : undefined}
          name={question.id}
          render={({ field, formState }) => (
            <FormControl
              {...field}
              component="fieldset"
              disabled={disabled}
              error={!!formState.errors[question.id]}
            >
              <RadioGroup aria-label="single choice" value={field.value || null} onChange={field.onChange}>
                <Box m={1} flexWrap="wrap">
                  {question.answer.options?.map((option: { value: string; label: string; id: string }) => (
                    <FormControlLabel
                      value={option.label}
                      key={option.id}
                      control={<AppRoundedRadio color="primary" name={option.label} />}
                      label={option.label}
                    />
                  ))}
                </Box>
              </RadioGroup>
              {!!formState.errors[question.id] && (
                <FormHelperText error>
                  {String(formState.errors[question.id]?.message || REQUIRED_MSG)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {question.answer.type === 'multiple_choice' && (
        <Controller
          control={control}
          rules={
            question.answer.required
              ? {
                  validate: (v: unknown) => (Array.isArray(v) && v.length > 0) || REQUIRED_MSG,
                }
              : undefined
          }
          name={question.id}
          defaultValue={[]}
          render={({ field, formState }) => {
            return (
              <FormControl component="fieldset" disabled={disabled} error={!!formState.errors[question.id]}>
                <Box m={1} flexWrap="wrap">
                  {question.answer.options?.map((option: { value: string; label: string; id: string }) => (
                    <FormControlLabel
                      key={option.id}
                      control={
                        <AppCheckbox
                          size="medium"
                          onChange={(e: any) => {
                            const currentValue = Array.isArray(field.value) ? field.value : [];
                            const checked = e.target.checked;
                            if (checked) {
                              const newValue = [...currentValue, option.label];
                              field.onChange(newValue);
                            } else {
                              const newValue = currentValue.filter(
                                (value: string) => value !== option.label
                              );
                              field.onChange(newValue);
                            }
                          }}
                          color="primary"
                          checked={Array.isArray(field.value) ? field.value.includes(option.label) : false}
                          variant="rectangle"
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </Box>
                {!!formState.errors[question.id] && (
                  <FormHelperText error>
                    {String(formState.errors[question.id]?.message || REQUIRED_MSG)}
                  </FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
      )}

      {question.answer.type === 'linear_scale' && (
        <Controller
          control={control}
          rules={
            question.answer.required
              ? {
                  validate: (v: unknown) => (typeof v === 'number' && !Number.isNaN(v)) || REQUIRED_MSG,
                }
              : undefined
          }
          name={question.id}
          render={({ field, formState }) => (
            <FormControl
              style={{ width: '100%' }}
              {...field}
              component="fieldset"
              error={!!formState.errors[question.id]}
            >
              <RadioGroup aria-label="single choice" value={field.value || null}>
                <Box m={1} display="flex" flexWrap="wrap" justifyContent="center">
                  {Array.from(
                    { length: question.answer.max ? +question.answer.max + 1 : 0 },
                    (_, i) => i
                  ).map((index: number) => (
                    <Box key={index} display="flex" flexDirection="column" alignItems="center">
                      <FormControlLabel
                        value={index}
                        control={
                          <AppRoundedRadio
                            disabled={disabled}
                            onChange={(e: any) => field.onChange(index)}
                            checked={field.value == index}
                            style={{ marginLeft: 4 }}
                            color="primary"
                            name={index.toString()}
                          />
                        }
                        label={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column-reverse',
                        }}
                      />
                      {(index == 0 || index == question.answer.max) && (
                        <Typography variant="body2">
                          {index == 0 ? question.answer.minLabel : question.answer.maxLabel}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </RadioGroup>
              {!!formState.errors[question.id] && (
                <FormHelperText error>
                  {String(formState.errors[question.id]?.message || REQUIRED_MSG)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {question.answer.type === 'date' && (
        <ControlledDateField
          rules={question.answer.required ? { required: REQUIRED_MSG } : undefined}
          name={question.id}
          disabled={disabled}
          label={'Date'}
        />
      )}

      {question.answer.type === 'photo' && (
        <>
          <Controller
            name={question.id}
            control={control}
            rules={getPhotoRules(question.answer.required)}
            render={({ field, formState }) => {
              const photoValue = mergePhotoAnswerValue(field.value);
              const photoHasError = !!formState.errors[question.id];

              const handlePhotoFiles = async (files: FileList | null) => {
                const file = files?.[0];
                if (!file?.type.startsWith('image/') || disabled) {
                  return;
                }
                setPhotoUploading(true);
                try {
                  const compressed = await compressFile(file);
                  const result = await uploadFileRequest(compressed);
                  const url = result?.data?.url as string | undefined;
                  if (!url) {
                    return;
                  }
                  const next: FormAnswerMediaItem = {
                    url,
                    name: file.name,
                    type: file.type,
                    size: compressed.size,
                  };
                  const base = mergePhotoAnswerValue(field.value);
                  field.onChange({ ...base, media: [...base.media, next] });
                } finally {
                  setPhotoUploading(false);
                }
              };

              return (
                <>
                  <AppTextField
                    ref={field.ref}
                    name={field.name}
                    onBlur={field.onBlur}
                    value={photoValue.comment}
                    onChange={e => {
                      const base = mergePhotoAnswerValue(field.value);
                      field.onChange({ ...base, comment: e.target.value });
                    }}
                    disabled={disabled}
                    error={photoHasError}
                    label="Comment"
                    fullWidth
                    margin="normal"
                    helperText={
                      photoHasError
                        ? String(formState.errors[question.id]?.message || REQUIRED_MSG)
                        : undefined
                    }
                  />

                  <input
                    ref={photoFileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={e => {
                      void handlePhotoFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />

                  <Box display="flex" alignItems="center" gridGap={12} marginTop={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      color={photoHasError ? 'secondary' : 'primary'}
                      style={{
                        width: 120,
                        ...(photoHasError && {
                          borderColor: theme.palette.error.main,
                          color: theme.palette.error.main,
                        }),
                      }}
                      disabled={disabled || photoUploading}
                      onClick={() => photoFileInputRef.current?.click()}
                    >
                      <ButtonTitleWithLoading
                        title="Add photo"
                        loaderVariant="primary"
                        loading={photoUploading}
                      />
                    </Button>
                  </Box>

                  <Box display="flex" flexWrap="wrap" gridGap={12}>
                    {photoValue.media.map((media, index) => (
                      <Box key={`${media.url}-${index}`} position="relative">
                        <FormResultMediaPreview media={[media]} title={question.title} />
                        {!disabled && (
                          <IconButton
                            size="small"
                            aria-label="Remove photo"
                            onClick={() => {
                              const base = mergePhotoAnswerValue(field.value);
                              field.onChange({
                                ...base,
                                media: base.media.filter((_, i) => i !== index),
                              });
                            }}
                            style={{
                              position: 'absolute',
                              top: 20,
                              right: 4,
                              backgroundColor: theme.palette.background.paper,
                              padding: 4,
                            }}
                          >
                            <IoCloseOutline color={theme.palette.text.primary} size={18} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                </>
              );
            }}
          />
        </>
      )}

      {question.answer.type === 'time' && (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <Controller
            name={question.id}
            control={control}
            rules={getTimeRules(question.answer.required)}
            render={({ field, formState }) => (
              <AppKeyboardTimePicker
                ampm={true}
                disabled={disabled}
                mask="__:__ _M"
                value={field.value || null}
                onChange={field.onChange}
                label="Time"
                fullWidth
                error={!!formState.errors[question.id]}
                helperText={
                  formState.errors[question.id]?.message
                    ? String(formState.errors[question.id]?.message)
                    : undefined
                }
              />
            )}
          />
        </MuiPickersUtilsProvider>
      )}

      {signatureBase64 && <FormSignature signatureBase64={signatureBase64} />}
    </Box>
  );
};
